import { useState } from 'react';
import { useThreadRuntime } from '@assistant-ui/react';
import { Check, ExternalLink } from 'lucide-react';
import { type Checkpoint, checkpointAnswer, showHumanUrl } from '@/lib/checkpointBlock';
import { useStudioSummary } from '@/components/studio/studioHooks';
import { QuestionButton, QuestionCard } from '@/components/studio/plan/Thread';
import { studioBOn } from '@/lib/studioB/flag';

/** The two stops every video makes get a title and a clearer button; the answer sent never changes. */
const STOPS: Record<string, { title: string; confirm: string }> = {
  'outline-review': { title: 'Plan ready for you', confirm: 'Build it' },
  'preview-review': { title: 'Your video is ready to watch', confirm: 'Looks good' },
};

/**
 * The chat's face of an `ivy-checkpoint` block. Only the latest stop, once its turn has ended
 * (`active`), is answerable; an earlier one (`stale`) reads as answered. A click sends the
 * contract's answer through the thread composer, the path a typed reply takes.
 */
export function CheckpointCard({ checkpoint, active, stale, answer }: { checkpoint: Checkpoint; active: boolean; stale: boolean; answer?: string }) {
  const thread = useThreadRuntime();
  const summary = useStudioSummary();
  const [sent, setSent] = useState<string | null>(null);
  const answerable = active && !sent;
  const stop = STOPS[checkpoint.id];
  const facts = stop && summary
    ? [summary.scenes > 0 && `${summary.scenes} scenes`, summary.durationSec && `about ${Math.round(summary.durationSec)} s`].filter(Boolean) as string[]
    : [];

  const send = (optionId?: string) => {
    if (!answerable) return;
    const text = checkpointAnswer(checkpoint, optionId);
    setSent(text);
    thread.composer.setText(text);
    thread.composer.send();
  };

  const humanUrl = showHumanUrl(checkpoint.humanUrl, window.location.href) ? checkpoint.humanUrl : undefined;

  if (studioBOn() && stale && !sent) {
    // F4: a question a later message answered folds to one line saying how.
    return (
      <p className="!my-1 inline-flex items-center gap-1.5 text-b-meta text-b-t3">
        <Check aria-hidden className="h-3 w-3" />
        <span data-answered>{answer ? `You answered: ${answer}` : 'Answered'}</span>
      </p>
    );
  }

  if (studioBOn()) {
    const hint = sent
      ? <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" />Sent: {sent}</span>
      : stale
        ? 'Answered in a later message.'
        : active
          ? checkpoint.kind === 'confirm' ? 'Or type what to change below.' : 'Or type your own answer below.'
          : undefined;
    return (
      <QuestionCard title={stop && summary ? stop.title : undefined} facts={facts.join(' · ') || undefined} hint={hint}>
        {checkpoint.kind === 'confirm' && (
          <QuestionButton label={stop?.confirm ?? 'Yes, go ahead'} recommended={checkpoint.recommended === 'yes'} disabled={!answerable} onClick={() => send()} />
        )}
        {checkpoint.kind === 'choice' && checkpoint.options?.map((o) => (
          <span key={o.id} className="inline-flex items-center gap-1">
            <QuestionButton label={o.label} recommended={o.id === checkpoint.recommended} disabled={!answerable} onClick={() => send(o.id)} />
            {o.previewUrl && (
              <a href={o.previewUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-b-t3 hover:text-b-t1" title="Preview">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </span>
        ))}
        {checkpoint.kind === 'text' && (
          <QuestionButton label={`Use suggestion: “${checkpoint.recommended}”`} recommended disabled={!answerable} onClick={() => send()} />
        )}
        {humanUrl && (
          <a href={humanUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-1 text-b-meta text-b-t2 hover:text-b-t1">
            <ExternalLink className="h-3.5 w-3.5" />
            {/\/video\//.test(humanUrl) ? 'Watch preview' : 'Open'}
          </a>
        )}
      </QuestionCard>
    );
  }

  return (
    <div className={`my-3 max-w-md rounded-xl border bg-card ${stop && summary ? 'border-foreground/20 px-4 py-3.5' : 'border-border px-3 py-2.5'}`}>
      {stop && summary && (
        <div className="mb-3">
          <h3 className="font-display text-[15px] font-semibold leading-5 text-foreground">{stop.title}</h3>
          {facts.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{facts.join(' · ')}</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {checkpoint.kind === 'confirm' && (
          <ActionButton label={stop?.confirm ?? 'Yes, go ahead'} recommended={checkpoint.recommended === 'yes'} disabled={!answerable} onClick={() => send()} />
        )}
        {checkpoint.kind === 'choice' && checkpoint.options?.map(o => (
          <span key={o.id} className="inline-flex items-center gap-1">
            <ActionButton label={o.label} recommended={o.id === checkpoint.recommended} disabled={!answerable} onClick={() => send(o.id)} />
            {o.previewUrl && (
              <a href={o.previewUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-muted-foreground hover:text-foreground" title="Preview">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </span>
        ))}
        {checkpoint.kind === 'text' && (
          <ActionButton label={`Use suggestion: “${checkpoint.recommended}”`} recommended disabled={!answerable} onClick={() => send()} />
        )}
        {humanUrl && (
          <a href={humanUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors">
            <ExternalLink className="w-3 h-3" />
            {/\/video\//.test(humanUrl) ? 'Watch preview' : 'Open'}
          </a>
        )}
      </div>
      {(sent || active || stale) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {sent
            ? <span className="inline-flex items-center gap-1"><Check className="w-3 h-3" />Sent: {sent}</span>
            : stale
              ? 'Answered in a later message.'
              : checkpoint.kind === 'confirm' ? 'Or type what to change below.' : 'Or type your own answer below.'}
        </p>
      )}
    </div>
  );
}

function ActionButton({ label, recommended, disabled, onClick }: { label: string; recommended: boolean; disabled: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors disabled:opacity-50 disabled:cursor-default ${
        recommended ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'border border-border hover:bg-muted'
      }`}
    >
      {label}
    </button>
  );
}
